import { inject, Injectable } from '@angular/core';
import { FILE_PREVIEW_ERROR, injectError } from '@quipex/core/http';
import { MonitoringService } from '@quipex/core/logging';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import {
    AIApiService,
    BuildingDocumentsApiService,
    type GetDocumentViewUrlResponse,
    type DocumentIntelligenceDocumentResponse,
    type UnifiedAIRequest,
    type UnifiedAIResponse,
} from '@quipex/shared/contracts';
import {
    catchError,
    map,
    shareReplay,
    tap,
    throwError,
    type Observable,
} from 'rxjs';
import { QUIPPY_PACK_ACTIONS_ENABLED } from './pack-actions/config';
import { QuippyPackActions } from './pack-actions/pack-actions';
import { isCanonicalDocumentId } from './source-document-id';

/** Thin transport wrapper over AIApiService scoped to the Quippy assistant slice. */
@Injectable()
export class QuippyApi {
    private readonly packActions = inject(QuippyPackActions, {
        optional: true,
    });
    private readonly actionsEnabled = inject(QUIPPY_PACK_ACTIONS_ENABLED);
    public packActionsEnabled(): boolean {
        return this.actionsEnabled();
    }

    private readonly aiApi = inject(AIApiService);
    private readonly documentsApi = inject(BuildingDocumentsApiService);
    private readonly monitoringService = inject(MonitoringService);
    private readonly clientError = injectError();

    private readonly documentMetadata = new Map<
        string,
        Observable<QuippySourceMetadata>
    >();

    public getSourceMetadata(
        buildingId: string,
        documentId: string
    ): Observable<QuippySourceMetadata> {
        if (!isCanonicalDocumentId(documentId))
            return throwError(() => new Error('Invalid document identifier'));
        const key = `${buildingId}/${documentId}`;
        const cached = this.documentMetadata.get(key);
        if (cached) return cached;
        const options = {
            context: createHttpContextFromOptions({
                // The hover card shows an inline unavailable state; a toast would interrupt passive source inspection.
                suppressAutoErrorToast: true,
            }),
        };
        const result = this.documentsApi
            .getBuildingDocumentIntelligence(
                buildingId,
                documentId,
                { includeViewUrl: false },
                options
            )
            .pipe(
                map((response) => ({
                    ...response.document,
                    aiSummary: response.document.aiSummary ?? response.summary,
                })),
                catchError((error) => {
                    this.documentMetadata.delete(key);
                    return throwError(() => error);
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        this.documentMetadata.set(key, result);
        return result;
    }

    public ask(payload: UnifiedAIRequest): Observable<UnifiedAIResponse> {
        const request =
            payload.documentActionContext && this.packActions
                ? {
                      ...payload,
                      documentActionContext: this.packActions.selectedContext(
                          payload.documentActionContext
                      ),
                  }
                : payload;
        return this.aiApi
            .askAssistant(request)
            .pipe(tap((response) => this.recordInvalidSourceIds(response)));
    }

    public getDocumentViewUrl(
        buildingId: string,
        documentId: string | null
    ): Observable<GetDocumentViewUrlResponse> {
        if (!isCanonicalDocumentId(documentId)) {
            return throwError(() =>
                this.clientError.create(
                    FILE_PREVIEW_ERROR.InvalidSourceIdentifier,
                    {
                        urnParts: [
                            'service',
                            'quippy-api',
                            'get-document-view-url',
                        ],
                        consoleErrorRestArgs: [],
                    }
                )
            );
        }

        return this.documentsApi.getBuildingDocumentViewUrl(
            buildingId,
            documentId,
            {
                context: createHttpContextFromOptions({
                    // The related-document action owns inaccessible-source and retry feedback.
                    suppressAutoErrorToast: true,
                }),
            }
        );
    }

    private recordInvalidSourceIds(response: UnifiedAIResponse): void {
        const invalidSourceIdCount = response.files.filter(
            (file) => !isCanonicalDocumentId(file.id)
        ).length;

        if (invalidSourceIdCount === 0) return;

        this.monitoringService.logEvent('QuippySourcePreviewSuppressed', {
            suppressed: true,
            sourceCount: response.files.length,
            suppressedSourceCount: invalidSourceIdCount,
        });
    }
}

export type QuippySourceMetadata = DocumentIntelligenceDocumentResponse;
